import React, { useState, useEffect } from 'react';
import { useStyles } from './SignupForm.styles';
import type { Option } from '@branchjs/neo-components';
import {
  Button,
  Checkbox,
  InputField,
  InputTitle,
  Grid,
  Icon,
  IconButton,
  Link,
  SelectDropdownField,
  Typography,
} from '@branchjs/neo-components';
import type { SignupState } from './SignupForm.types';
import { RoleOptions } from './SignupForm.types';
import LoggedoutActions from '../../../../main/actions/LoggedoutActions';
import LoggedoutStore from '../../../../main/stores/LoggedoutStore';
import {
  firstNameValid,
  lastNameValid,
  inputTooLong,
  emailValid,
  validBusinessEmail,
  passwordsMatch,
  roleSelected,
  termsTickbox,
  companyNameValid,
  urlValid,
} from '../../ScreenSignupValidation';
import {
  LEGAL_TERMS_CONDITIONS_LINK,
  LEGAL_PRIVACY_POLICY_LINK,
  LEGAL_PRIVACY_OPTOUT_LINK,
  TERMS_CONSENT_DB_FIELD,
} from '../../../../common/support/gdprConstants';
import { guess, getCurrentTimeInUTC } from '../../../../main/support/dateUtils';
import { PasswordCheck } from './PasswordCheck/PasswordCheck';
import { FormHelperText } from '@mui/material';
import { PasswordValidator } from '@branchjs/tooling';
import RecaptchaEnterprise from '../../../../common/components/core/RecaptchaEnterprise';
import LoggedOutContainer from '../../components/LoggedOutContainer/LoggedOutContainer';
import {
  ACCOUNT_VALIDATION_KEYS,
  ACCOUNT_VALIDATION_MESSAGES,
} from '../../../../../../shared/constants/accountValidationConstants';
import { KEY_KEYPRESS } from './SignupForm.constants';

export function SignupForm() {
  const getRecaptchaKey = () => {
    return (window as any).RECAPTCHA_KEY;
  };

  const getCountry = () => {
    return (window as any).App?.context?.country || {};
  };

  const [signupState, setSignupState] = useState<SignupState>({
    errors: LoggedoutStore.getErrors(),
    name: null,
    last_name: null,
    email: null,
    password: null,
    password2: null,
    role: null,
    company_name: null,
    company_url: null,
    acceptedTerms: false,
    marketingOptOut: false,
    lowercase: false,
    capital: false,
    number: false,
    symbol: false,
    min: false,
    passwordMatch: false,
    passwordChecked: false,
    password2Checked: false,
    errorFootnotes: {
      [ACCOUNT_VALIDATION_KEYS.NAME]: null,
      [ACCOUNT_VALIDATION_KEYS.LAST_NAME]: null,
      [ACCOUNT_VALIDATION_KEYS.EMAIL]: null,
      [ACCOUNT_VALIDATION_KEYS.PASSWORD]: null,
      [ACCOUNT_VALIDATION_KEYS.CONFIRM_PASSWORD]: null,
      [ACCOUNT_VALIDATION_KEYS.ROLE]: null,
      [ACCOUNT_VALIDATION_KEYS.TERMS]: false,
      [ACCOUNT_VALIDATION_KEYS.COMPANY_NAME]: null,
      [ACCOUNT_VALIDATION_KEYS.COMPANY_URL]: null,
    },
  });
  const [recaptchaState, setRecaptchaState] = useState<string>('');
  const [primaryRole, setPrimaryRole] = useState<Option | Option[]>([]);
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
  const validator: PasswordValidator = new PasswordValidator();
  const classes = useStyles();
  const country = getCountry();
  const recaptchaKey = getRecaptchaKey();

  const updateSignupState = (changes: any) => {
    const currentState = { ...signupState, ...changes };
    setSignupState(currentState);
    return currentState;
  };

  // this is here to determine which to show in the input footnote; the frontend errors (errorFootnotes) or
  // the errors returned from the backend (signupState.errors) which are generated on CTA button click.
  const _renderErrors = (key: string) => {
    if (key === ACCOUNT_VALIDATION_KEYS.PASSWORD && signupState.errorFootnotes[key] == ACCOUNT_VALIDATION_KEYS.ERROR) {
      // Branch Neo components expects 'true' to show component as error without showing a message.
      // We override the message in helperText to show the validation results.
      return true;
    }

    return signupState.errorFootnotes[key] ? signupState.errorFootnotes[key] : signupState.errors[key];
  };

  const _handleNameChange = (e: any, key: string) => {
    const input = e.target.value;
    const changes = { [key]: input, errorFootnotes: signupState.errorFootnotes };

    if (key === ACCOUNT_VALIDATION_KEYS.NAME) {
      changes.errorFootnotes[key] = !firstNameValid(input)
        ? ACCOUNT_VALIDATION_MESSAGES.SPECIFY_FIRST_NAME
        : inputTooLong(input)
        ? ACCOUNT_VALIDATION_MESSAGES.SPECIFY_SHORTER_FIRST_NAME
        : '';
    } else if (key === ACCOUNT_VALIDATION_KEYS.LAST_NAME) {
      changes.errorFootnotes[key] = !lastNameValid(input)
        ? ACCOUNT_VALIDATION_MESSAGES.SPECIFY_LAST_NAME
        : inputTooLong(input)
        ? ACCOUNT_VALIDATION_MESSAGES.SPECIFY_SHORTER_LAST_NAME
        : '';
    }
    updateSignupState(changes);
    LoggedoutActions.clearErrors();
  };

  const _handleEmailChange = (e: any) => {
    const input = e.target.value;
    const changes = { email: input, errorFootnotes: signupState.errorFootnotes };

    changes.errorFootnotes[ACCOUNT_VALIDATION_KEYS.EMAIL] = !emailValid(input)
      ? ACCOUNT_VALIDATION_MESSAGES.SPECIFY_VALID_EMAIL
      : !validBusinessEmail(input)
      ? ACCOUNT_VALIDATION_MESSAGES.SPECIFY_VALID_BUSINESS_EMAIL
      : inputTooLong(input)
      ? ACCOUNT_VALIDATION_MESSAGES.SPECIFY_SHORTER_EMAIL
      : '';
    updateSignupState(changes);
    LoggedoutActions.clearErrors();
  };

  const _handlePasswordChange = (e: any) => {
    const input = e.target.value;
    const changes = {
      password: input,
      password2Checked: false,
      passwordChecked: true,
      errorFootnotes: signupState.errorFootnotes,
    };
    const isValid = validator.validate(input);

    changes.errorFootnotes[ACCOUNT_VALIDATION_KEYS.PASSWORD] = !input
      ? ACCOUNT_VALIDATION_MESSAGES.SPECIFY_PASSWORD
      : !isValid
      ? ACCOUNT_VALIDATION_KEYS.ERROR
      : '';
    updateSignupState(changes);
    LoggedoutActions.clearErrors();
  };

  const _renderPasswordSuccessIcon = () => {
    return signupState.password && validator.validate(signupState.password);
  };

  const _handlePassword2Change = (e: any) => {
    const input = e.target.value;
    const changes = {
      password2: input,
      password2Checked: true,
      passwordMatch: false,
      errorFootnotes: signupState.errorFootnotes,
    };

    if (!!_renderPasswordSuccessIcon()) {
      changes.errorFootnotes[ACCOUNT_VALIDATION_KEYS.CONFIRM_PASSWORD] = !passwordsMatch(String(signupState.password), input)
        ? ACCOUNT_VALIDATION_MESSAGES.PASSWORDS_DONT_MATCH
        : '';
      changes.passwordMatch = !!passwordsMatch(String(signupState.password), input) ? true : false;
    } else {
      changes.errorFootnotes[ACCOUNT_VALIDATION_KEYS.CONFIRM_PASSWORD] = ACCOUNT_VALIDATION_MESSAGES.PASSWORD_INVALID;
    }
    updateSignupState(changes);
    LoggedoutActions.clearErrors();
  };

  const _handleRoleChange = (options: Option | Option[]) => {
    if (!Array.isArray(options)) {
      setPrimaryRole(options);
      const input = options.value;
      const changes = { role: input, errorFootnotes: signupState.errorFootnotes };

      changes.errorFootnotes[ACCOUNT_VALIDATION_KEYS.ROLE] = !roleSelected(input)
        ? ACCOUNT_VALIDATION_MESSAGES.SPECIFY_ROLE
        : '';
      updateSignupState(changes);
      LoggedoutActions.clearErrors();
    }
  };

  const _handleTermsChange = (e: any) => {
    const input = e.target.checked;
    const changes = { acceptedTerms: input, errorFootnotes: signupState.errorFootnotes };

    changes.errorFootnotes[ACCOUNT_VALIDATION_KEYS.TERMS] = !termsTickbox(input)
      ? ACCOUNT_VALIDATION_MESSAGES.ACCEPT_TERMS
      : '';
    updateSignupState(changes);
    LoggedoutActions.clearErrors();
  };

  const _handleMarketingChange = (e: any) => {
    updateSignupState({ marketingOptOut: e.target.checked });
  };

  const _setRecaptchaResonse = (recaptcha_response?: string) => {
    setRecaptchaState(recaptcha_response ?? '');
  };

  const _handleCompanyNameChange = (e: any) => {
    const input = e.target.value;
    const changes = { company_name: input, errorFootnotes: signupState.errorFootnotes };

    changes.errorFootnotes[ACCOUNT_VALIDATION_KEYS.COMPANY_NAME] = !companyNameValid(input)
      ? ACCOUNT_VALIDATION_MESSAGES.SPECIFY_COMPANY_NAME
      : inputTooLong(input)
      ? ACCOUNT_VALIDATION_MESSAGES.SPECIFY_SHORTER_COMPANY_NAME
      : '';
    updateSignupState(changes);
    LoggedoutActions.clearErrors();
  };

  const _handleCompanyUrlChange = (e: any) => {
    const input = e.target.value;
    const changes = { company_url: input, errorFootnotes: signupState.errorFootnotes };

    changes.errorFootnotes[ACCOUNT_VALIDATION_KEYS.COMPANY_URL] = !urlValid('http://' + input)
      ? ACCOUNT_VALIDATION_MESSAGES.SPECIFY_COMPANY_URL
      : inputTooLong(input)
      ? ACCOUNT_VALIDATION_MESSAGES.SPECIFY_SHORTER_COMPANY_URL
      : '';
    updateSignupState(changes);
    LoggedoutActions.clearErrors();
  };

  /**
   * This allows us to set all the errors at once, if a user clicks on the CTA button without
   * filling in any inputs.
   */
  const _setAllErrors = (userInput: (string | false)[]) => {
    const errorMap = {
      [ACCOUNT_VALIDATION_KEYS.NAME]: ACCOUNT_VALIDATION_MESSAGES.SPECIFY_FIRST_NAME,
      [ACCOUNT_VALIDATION_KEYS.LAST_NAME]: ACCOUNT_VALIDATION_MESSAGES.SPECIFY_LAST_NAME,
      [ACCOUNT_VALIDATION_KEYS.EMAIL]: ACCOUNT_VALIDATION_MESSAGES.SPECIFY_VALID_EMAIL,
      [ACCOUNT_VALIDATION_KEYS.PASSWORD]: ACCOUNT_VALIDATION_MESSAGES.SPECIFY_PASSWORD,
      [ACCOUNT_VALIDATION_KEYS.CONFIRM_PASSWORD]: ACCOUNT_VALIDATION_MESSAGES.PASSWORDS_DONT_MATCH,
      [ACCOUNT_VALIDATION_KEYS.ROLE]: ACCOUNT_VALIDATION_MESSAGES.SPECIFY_ROLE,
      [ACCOUNT_VALIDATION_KEYS.TERMS]: ACCOUNT_VALIDATION_MESSAGES.ACCEPT_TERMS,
      [ACCOUNT_VALIDATION_KEYS.COMPANY_NAME]: ACCOUNT_VALIDATION_MESSAGES.SPECIFY_COMPANY_NAME,
      [ACCOUNT_VALIDATION_KEYS.COMPANY_URL]: ACCOUNT_VALIDATION_MESSAGES.SPECIFY_COMPANY_URL,
    };
    const changes = { errorFootnotes: signupState.errorFootnotes };
    userInput.forEach((field) => {
      if (!!field) {
        changes.errorFootnotes[field] = errorMap[field];
      }
    });
    updateSignupState(changes);
  };

  /**
   * This will check to see if input fields have a value.
   * userInput is an array which will contain either the name of the rule/input if it is empty,
   * false if it is not empty.
   */
  const _emptyFields = () => {
    const userInput = Object.keys(signupState.errorFootnotes).map((rule) => {
      if (rule === ACCOUNT_VALIDATION_KEYS.TERMS) {
        return !signupState.acceptedTerms ? rule : false;
      } else {
        return !signupState[rule] ? rule : false;
      }
    });
    _setAllErrors(userInput);
    return userInput;
  };

  /**
   * This checks the inputs and returns true if every field has an input value.
   */
  const _allFieldsFilled = (fields: any[]) => {
    for (let i = 0; i < fields.length; i++) {
      if (fields[i] === null) {
        return false;
      }
    }
    return true;
  };

  const _ctaClick = () => {
    const { errors, acceptedTerms, ...data } = signupState;

    data[TERMS_CONSENT_DB_FIELD] = signupState.acceptedTerms ? getCurrentTimeInUTC() : null;
    if (!country.hasDataProtectionPolicy) {
      delete data.marketingOptOut;
    }

    const formInputs = _emptyFields();
    if (_allFieldsFilled(formInputs) && _renderPasswordSuccessIcon()) {
      LoggedoutActions.signup({ ...data, timezone: guess, country: country.name, recaptcha_response: recaptchaState });
    }
  };

  const _handleEnterKeyPress = (event: any) => {
    if (event.keyCode === 13) {
      _ctaClick();
    }
  };

  const _onChange = () => {
    const errors = LoggedoutStore.getErrors();
    updateSignupState({ errors });
  };

  useEffect(() => {
    window.addEventListener(KEY_KEYPRESS, _handleEnterKeyPress);
    LoggedoutStore.addChangeListener(_onChange);
    LoggedoutActions.clearErrors();

    return () => {
      window.removeEventListener(KEY_KEYPRESS, _handleEnterKeyPress);
      LoggedoutStore.removeChangeListener(_onChange);
    };
  }, [signupState, _handleEnterKeyPress, _onChange]);

  const wrapFormInputTitle: any = (title?: string) => {
    return <InputTitle title={title} />;
  };

  return (
    <LoggedOutContainer title="Sign up" linkTitle="Existing user?" linkText="Sign in" linkPathName="/login">
      <form className={classes.form}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <InputField
              type="text"
              title="First name"
              onChange={(e) => _handleNameChange(e, ACCOUNT_VALIDATION_KEYS.NAME)}
              value={signupState.name || ''}
              name="name"
              id="name"
              error={_renderErrors(ACCOUNT_VALIDATION_KEYS.NAME)}
              placeholder="First name"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InputField
              type="text"
              title="Last name"
              onChange={(e) => _handleNameChange(e, ACCOUNT_VALIDATION_KEYS.LAST_NAME)}
              value={signupState.last_name || ''}
              name="last_name"
              id="last_name"
              error={_renderErrors(ACCOUNT_VALIDATION_KEYS.LAST_NAME)}
              placeholder="Last name"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InputField
              type="text"
              title="Company"
              onChange={(e) => _handleCompanyNameChange(e)}
              value={signupState.company_name || ''}
              name="company_name"
              id="company_name"
              error={_renderErrors(ACCOUNT_VALIDATION_KEYS.COMPANY_NAME)}
              placeholder="Company"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InputField
              type="text"
              title="Website"
              onChange={(e) => _handleCompanyUrlChange(e)}
              value={signupState.company_url || ''}
              name="company_url"
              id="company_url"
              error={_renderErrors(ACCOUNT_VALIDATION_KEYS.COMPANY_URL)}
              placeholder="example.com"
              fullWidth
              customStartAdornment={<Typography variant="body2">http://</Typography>}
            />
          </Grid>
          <Grid item xs={12}>
            <InputField
              type="text"
              title="Work email"
              onChange={_handleEmailChange}
              value={signupState.email || ''}
              name="email"
              id="email"
              error={_renderErrors(ACCOUNT_VALIDATION_KEYS.EMAIL)}
              placeholder="Enter your work email"
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            {/* TODO: NEED TO FIGURE OUT HOW TO ADD 'displayShowHide=true' */}
            {/* TODO: NEED TO FIGURE OUT HOW TO ADD 'pasteDisabled=true' */}
            <InputField
              type={passwordVisible ? 'text' : 'password'}
              title="Create password"
              onChange={_handlePasswordChange}
              value={signupState.password || ''}
              name="password"
              id="password-input"
              error={_renderErrors(ACCOUNT_VALIDATION_KEYS.PASSWORD)}
              success={signupState.passwordChecked && !_renderErrors(ACCOUNT_VALIDATION_KEYS.PASSWORD)}
              autoComplete="new-password"
              placeholder="Enter your password"
              fullWidth
              customTitleEndAdornment={
                <IconButton
                  id="showHidePasswordButton"
                  size="small"
                  color={passwordVisible ? 'primary' : 'secondary'}
                  edge="end"
                  onClick={() => setPasswordVisible(!passwordVisible)}
                >
                  <Icon id="showHidePasswordIcon">{passwordVisible ? 'visibility' : 'visibility_off'}</Icon>
                  <Typography variant="body2" color="primary" className={classes.showHidePasswordLink}>
                    {passwordVisible ? 'Hide' : 'Show'}
                  </Typography>
                </IconButton>
              }
            />
            <div data-testid="password-validations-container">
              <PasswordCheck
                password={signupState.password}
                validator={validator}
                errors={signupState.errors}
                errorFootnotes={signupState.errorFootnotes}
              />
            </div>
          </Grid>
          <Grid item xs={12}>
            <InputField
              type="password"
              title="Confirm password"
              onChange={_handlePassword2Change}
              value={signupState.password2 || ''}
              name="password2"
              id="password2"
              error={_renderErrors(ACCOUNT_VALIDATION_KEYS.CONFIRM_PASSWORD)}
              success={signupState.passwordMatch}
              autoComplete="new-password"
              placeholder="Enter your password"
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            {/* ONBLUR NOT SUPPORTED IN BRANCH-NEO */}
            <SelectDropdownField
              // name="signup-role"
              title={wrapFormInputTitle('Primary role')}
              id="signup-role"
              options={RoleOptions}
              onChange={_handleRoleChange}
              placeholder="Choose your primary role"
              // onBlur={() => _validateRole(signupState.role)}
              customStyles={{ width: '100%' }}
              value={primaryRole}
            />
            <FormHelperText id="signup-role-error-message" className={classes.error} error data-testid="role-error">
              {_renderErrors(ACCOUNT_VALIDATION_KEYS.ROLE)}
            </FormHelperText>
            <div className={classes.terms}>
              <Checkbox
                checked={signupState.acceptedTerms}
                onChange={_handleTermsChange}
                id="terms"
                color="primary"
                // onBlur={_validateTerms}
              />
              <label className={classes.label} htmlFor="terms">
                <Typography variant="body2">
                  <span>I agree to Branch's </span>
                  <Link href={`${LEGAL_TERMS_CONDITIONS_LINK}`} id="terms-and-conditions-link">
                    Terms & Conditions
                  </Link>
                  <span> and </span>
                  <Link href={`${LEGAL_PRIVACY_POLICY_LINK}`} id="privacy-policy-link">
                    Privacy Policy
                  </Link>
                  <span>.</span>
                </Typography>
              </label>
              <FormHelperText id="signup-terms-error-message" className={classes.error} error data-testid="terms-error">
                {_renderErrors(ACCOUNT_VALIDATION_KEYS.TERMS)}
              </FormHelperText>
            </div>
          </Grid>
          {country.hasDataProtectionPolicy ? (
            <Grid item xs={12} container alignItems="center">
              <Checkbox
                id="marketing_communications"
                checked={signupState.marketingOptOut}
                onChange={_handleMarketingChange}
              />
              <label htmlFor="marketing_communications">
                <Typography variant="body2">
                  By submitting your information, Branch will add you to its email list to keep you posted about Branch
                  products and promotions in accordance with our privacy policy.{' '}
                  <span className="h_fw-400 h_mb-10">
                    If you do not want to be added to that list, please check this box.
                  </span>
                </Typography>
              </label>
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <RecaptchaEnterprise
              siteKey={recaptchaKey}
              action="signup"
              onTokenReceived={_setRecaptchaResonse}
              onExpired={_setRecaptchaResonse}
            />
            <FormHelperText className={classes.error} error>
              {signupState.errors.recaptcha}
            </FormHelperText>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              className={classes.input}
              onClick={_ctaClick}
              id="sign-up"
              endIcon={<Icon>arrow_forward</Icon>}
            >
              Create account
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body3">
              By clicking "Create account," you acknowledge that Branch may send you information about our products and
              promotions. Visit our{' '}
              <Link href={LEGAL_PRIVACY_OPTOUT_LINK} id="sign-up-privacy-notice">
                information page
              </Link>{' '}
              to learn more about how to opt-out of our promotional emails.
            </Typography>
          </Grid>
        </Grid>
      </form>
    </LoggedOutContainer>
  );
}
